// src/App.tsx - Anti-Cheat Enhanced Rapid Response Game
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './lib/supabaseClient';
import TSOAuthDetector from './components/TSOAuthDetector';
import { ExternalLink, Trophy, BarChart, LogOut, HelpCircle } from 'lucide-react';

// Anti-cheat: Generate random class names per session
const generateRandomClasses = () => ({
  enemyContainer: `enemy-${Math.random().toString(36).substr(2, 9)}`,
  enemyIcon: `icon-${Math.random().toString(36).substr(2, 9)}`,
  enemyText: `text-${Math.random().toString(36).substr(2, 9)}`
});

// Anti-cheat: Word obfuscation utilities
const obfuscateWord = (word: string) => {
  const chars = word.split('');
  const segments = [];
  for (let i = 0; i < chars.length; i++) {
    segments.push({
      char: chars[i],
      id: Math.random().toString(36).substr(2, 9),
      delay: Math.random() * 100 // Random render delay
    });
  }
  return segments;
};

// Anti-cheat: Behavior monitoring
interface AntiCheatMetrics {
  keystrokes: number[];
  lastKeystroke: number;
  perfectStreak: number;
  averageInterval: number;
  suspiciousPatterns: string[];
  rapidFires: number;
}

// Types
interface EnemyWord {
  word_id: number;
  enemy_type: string;
  word_text: string;
  difficulty_level: number;
  word_length: number;
}

interface Enemy {
  id: string;
  type: string;
  word: string;
  obfuscatedSegments: Array<{char: string, id: string, delay: number}>;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  scale: number;
  isVisible: boolean;
  partialText?: string;
  spawnTime: number;
}

interface GameState {
  isPlaying: boolean;
  isPaused: boolean;
  score: number;
  wave: number;
  health: number;
  maxHealth: number;
  wpm: number;
  wordsTyped: number;
  correctWords: number;
  enemiesDestroyed: number;
  sessionStartTime: number;
  vlanKeeperActive: boolean;
  activeQuarter: number | null;
  nukesAvailable: number;
  firewallsAvailable: number;
  scanAvailable: number;
}

interface Profile {
  user_id: string;
  username: string;
  faction_id: number;
  cc_balance: number;
}

const ENEMY_TYPES = {
  malware: { icon: '🦠', color: 'text-red-400', bgColor: 'bg-red-900/20', borderColor: 'border-red-400' },
  ddos: { icon: '⚡', color: 'text-yellow-400', bgColor: 'bg-yellow-900/20', borderColor: 'border-yellow-400' },
  phishing: { icon: '🎣', color: 'text-purple-400', bgColor: 'bg-purple-900/20', borderColor: 'border-purple-400' },
  rootkit: { icon: '🔑', color: 'text-gray-400', bgColor: 'bg-gray-900/20', borderColor: 'border-gray-400' },
  ransomware: { icon: '🔒', color: 'text-red-500', bgColor: 'bg-red-900/30', borderColor: 'border-red-500' },
  bug: { icon: '🐛', color: 'text-green-400', bgColor: 'bg-green-900/20', borderColor: 'border-green-400' },
  mini_bug: { icon: '🐞', color: 'text-green-300', bgColor: 'bg-green-900/15', borderColor: 'border-green-300' },
  vlan_keeper: { icon: '🔀', color: 'text-orange-400', bgColor: 'bg-orange-900/20', borderColor: 'border-orange-400' }
};

const App: React.FC = () => {
  // Auth state
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Anti-cheat state
  const [randomClasses] = useState(generateRandomClasses());
  const [antiCheatMetrics, setAntiCheatMetrics] = useState<AntiCheatMetrics>({
    keystrokes: [],
    lastKeystroke: 0,
    perfectStreak: 0,
    averageInterval: 0,
    suspiciousPatterns: [],
    rapidFires: 0
  });
  const [isPlayerSuspicious, setIsPlayerSuspicious] = useState(false);

  // Game state
  const [gameState, setGameState] = useState<GameState>({
    isPlaying: false,
    isPaused: false,
    score: 0,
    wave: 1,
    health: 100,
    maxHealth: 100,
    wpm: 0,
    wordsTyped: 0,
    correctWords: 0,
    enemiesDestroyed: 0,
    sessionStartTime: 0,
    vlanKeeperActive: false,
    activeQuarter: null,
    nukesAvailable: 0,
    firewallsAvailable: 0,
    scanAvailable: 0
  });

  const [enemies, setEnemies] = useState<Enemy[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [enemyWords, setEnemyWords] = useState<EnemyWord[]>([]);
  const [gameStartTime, setGameStartTime] = useState<number>(0);
  const [lastWpmUpdate, setLastWpmUpdate] = useState<number>(0);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  const [leaderboardData, setLeaderboardData] = useState<{
    daily: any[];
    weekly: any[];
    userRank: { daily: number | null; weekly: number | null };
  }>({
    daily: [],
    weekly: [],
    userRank: { daily: null, weekly: null }
  });
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  
  const gameLoopRef = useRef<number | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);

  // Anti-cheat: Behavior analysis
  const analyzeKeystroke = useCallback((timestamp: number) => {
    setAntiCheatMetrics(prev => {
      const newKeystrokes = [...prev.keystrokes, timestamp].slice(-20); // Keep last 20 keystrokes
      const intervals = [];
      
      for (let i = 1; i < newKeystrokes.length; i++) {
        intervals.push(newKeystrokes[i] - newKeystrokes[i-1]);
      }
      
      const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      
      // Check for suspicious patterns
      const suspiciousPatterns = [...prev.suspiciousPatterns];
      
      // Too fast typing (less than 50ms between keystrokes consistently)
      if (intervals.length > 5 && intervals.every(interval => interval < 50)) {
        suspiciousPatterns.push('rapid_fire');
      }
      
      // Too regular intervals (robotic typing)
      if (intervals.length > 10) {
        const variance = intervals.reduce((acc, interval) => acc + Math.pow(interval - avgInterval, 2), 0) / intervals.length;
        if (variance < 100 && avgInterval < 200) { // Very consistent timing
          suspiciousPatterns.push('robotic_timing');
        }
      }
      
      return {
        ...prev,
        keystrokes: newKeystrokes,
        lastKeystroke: timestamp,
        averageInterval: avgInterval,
        suspiciousPatterns
      };
    });
  }, []);

  // Anti-cheat: Check if player is suspicious
  useEffect(() => {
    const suspiciousThreshold = 3;
    const isSuspicious = antiCheatMetrics.suspiciousPatterns.length >= suspiciousThreshold ||
                        (gameState.wpm > 200 && gameState.wordsTyped > 20) ||
                        (antiCheatMetrics.perfectStreak > 15);
    
    setIsPlayerSuspicious(isSuspicious);
  }, [antiCheatMetrics, gameState.wpm, gameState.wordsTyped]);

  // Auth functions
  const checkAuth = useCallback(async () => {
    try {
      // DEV MODE: Bypass auth for development
      if (process.env.NODE_ENV === 'development') {
        const devUser = {
          user_id: 'dev-user-123',
          username: 'DevPlayer',
          faction_id: 1,
          cc_balance: 1000
        };
        setProfile(devUser);
        setIsAuthenticated(true);
        setAuthLoading(false);
        return;
      }

      // Check if we already have a valid Supabase session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
      }
      
      if (session?.user) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
        
        if (profileError) {
        }
        
        if (profileData) {
          setProfile(profileData);
          setIsAuthenticated(true);
          setAuthLoading(false);
          return;
        } else {
        }
      } else {
      }

      // If no session found, wait for TSOAuthDetector
      setTimeout(async () => {
        try {
          const { data: { session: newSession } } = await supabase.auth.getSession();
          
          if (newSession?.user) {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('*')
              .eq('user_id', newSession.user.id)
              .single();
            
            if (profileData) {
              setProfile(profileData);
              setIsAuthenticated(true);
            } else {
            }
          } else {
          }
        } catch (error) {
        } finally {
          setAuthLoading(false);
        }
      }, 1500); // Increased timeout to 1.5 seconds

    } catch (error) {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    (window as any).triggerAuthRecheck = () => {
      checkAuth();
    };
    return () => {
      delete (window as any).triggerAuthRecheck;
    };
  }, [checkAuth]);

  const logout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('sb-auth-token');
    setProfile(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Fetch enemy words from database
  const fetchEnemyWords = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('rapid_response_enemy_words')
        .select('*')
        .order('difficulty_level');
      
      if (error) {
        // Fallback words if database isn't set up yet
        setEnemyWords([
          { word_id: 1, enemy_type: 'malware', word_text: 'VIRUS', difficulty_level: 1, word_length: 5 },
          { word_id: 2, enemy_type: 'malware', word_text: 'TROJAN', difficulty_level: 2, word_length: 6 },
          { word_id: 3, enemy_type: 'ddos', word_text: 'SYNFLOOD', difficulty_level: 2, word_length: 8 },
          { word_id: 4, enemy_type: 'phishing', word_text: 'SPAM', difficulty_level: 1, word_length: 4 },
          { word_id: 5, enemy_type: 'rootkit', word_text: 'MALWARE', difficulty_level: 3, word_length: 7 },
          { word_id: 6, enemy_type: 'ransomware', word_text: 'DECRYPT', difficulty_level: 2, word_length: 7 },
          { word_id: 7, enemy_type: 'bug', word_text: 'OVERFLOW', difficulty_level: 3, word_length: 8 },
          { word_id: 8, enemy_type: 'vlan_keeper', word_text: 'SEGMENT', difficulty_level: 2, word_length: 7 }
        ]);
      } else {
        setEnemyWords(data || []);
      }
    } catch (err) {
      // Silent error handling
    }
  }, []);

  useEffect(() => {
    fetchEnemyWords();
  }, [fetchEnemyWords]);

  // Helper function to get Monday of the current week
  const getMondayOfWeek = (date: Date): string => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split('T')[0];
  };

  // Fetch leaderboard data
  const fetchLeaderboardData = useCallback(async () => {
    if (!profile?.user_id) return;
    
    setLeaderboardLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const mondayOfWeek = getMondayOfWeek(new Date());

      // Fetch daily leaderboard
      const { data: dailyData } = await supabase
        .from('rapid_response_daily_leaderboards')
        .select(`
          user_id,
          best_score,
          best_wpm,
          rank,
          cc_awarded,
          profiles(username, faction_id)
        `)
        .eq('date', today)
        .order('rank', { ascending: true })
        .limit(10);

      // Fetch weekly leaderboard  
      const { data: weeklyData } = await supabase
        .from('rapid_response_weekly_leaderboards')
        .select(`
          user_id,
          best_score,
          best_wpm,
          total_sessions,
          rank,
          cc_awarded,
          profiles(username, faction_id)
        `)
        .eq('week_start', mondayOfWeek)
        .order('rank', { ascending: true })
        .limit(10);

      // Get user's current rank
      const { data: userDailyRank } = await supabase
        .from('rapid_response_daily_leaderboards')
        .select('rank')
        .eq('user_id', profile.user_id)
        .eq('date', today)
        .single();

      const { data: userWeeklyRank } = await supabase
        .from('rapid_response_weekly_leaderboards')
        .select('rank')
        .eq('user_id', profile.user_id)
        .eq('week_start', mondayOfWeek)
        .single();

      setLeaderboardData({
        daily: dailyData || [],
        weekly: weeklyData || [],
        userRank: {
          daily: userDailyRank?.rank || null,
          weekly: userWeeklyRank?.rank || null
        }
      });

    } catch (error) {
      // Silent error handling
    } finally {
      setLeaderboardLoading(false);
    }
  }, [profile?.user_id]);

  // Load leaderboard when requested
  useEffect(() => {
    if (showLeaderboard && profile?.user_id) {
      fetchLeaderboardData();
    }
  }, [showLeaderboard, fetchLeaderboardData]);

  // Get random word for enemy type
  const getRandomWord = (enemyType: string): string => {
    const typeWords = enemyWords.filter(w => w.enemy_type === enemyType || w.enemy_type === enemyType.replace('_', ''));
    if (typeWords.length === 0) {
      // Fallback words
      const fallbacks: { [key: string]: string[] } = {
        malware: ['VIRUS', 'TROJAN', 'SPYWARE'],
        ddos: ['SYNFLOOD', 'PACKET', 'BANDWIDTH'],
        phishing: ['SPAM', 'URGENT', 'VERIFY'],
        rootkit: ['MALWARE', 'QUARANTINE', 'DEFENDER'],
        ransomware: ['DECRYPT', 'BITCOIN', 'LOCKED'],
        bug: ['OVERFLOW', 'SEGFAULT', 'BUFFER'],
        mini_bug: ['FIX', 'PATCH', 'DEBUG'],
        vlan_keeper: ['SEGMENT', 'NETWORK', 'VLAN']
      };
      const words = fallbacks[enemyType] || ['SECURITY'];
      return words[Math.floor(Math.random() * words.length)];
    }
    
    const selectedWord = typeWords[Math.floor(Math.random() * typeWords.length)];
    return selectedWord.word_text;
  };

  // Create partial text for rootkit
  const createPartialText = (word: string): string => {
    const chars = word.split('');
    return chars.map((char, index) => {
      return Math.random() < 0.4 ? char : '_';
    }).join('');
  };

  // Anti-cheat enhanced spawn enemy
  const spawnEnemy = useCallback(() => {
    if (!gameState.isPlaying || gameState.isPaused) return;

    const enemyTypes = Object.keys(ENEMY_TYPES);
    let randomType = enemyTypes[Math.floor(Math.random() * enemyTypes.length)];
    
    // Weighted enemy spawn chances (more realistic to cybersecurity)
    const random = Math.random();
    if (random < 0.35) {
      randomType = 'phishing'; // 35% - Most common, like real life!
    } else if (random < 0.55) {
      randomType = 'malware'; // 20% - Common
    } else if (random < 0.75) {
      randomType = 'ddos'; // 20% - Frequent attacks
    } else if (random < 0.85) {
      randomType = 'ransomware'; // 10% - Serious but less common
    } else if (random < 0.92) {
      randomType = 'bug'; // 7% - Development issues
    } else if (random < 0.97) {
      randomType = 'rootkit'; // 5% - Stealthy, rare
    } else {
      // VLAN keeper - reduced chance, only if none active
      if (!gameState.vlanKeeperActive && gameState.wave >= 2) {
        randomType = 'vlan_keeper'; // 3% and only wave 2+
      } else {
        randomType = 'phishing'; // Fallback to phishing
      }
    }
    
    const actualType = randomType;
    
    const container = gameContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;

    // Spawn from random edge
    const edge = Math.floor(Math.random() * 4);
    let startX, startY;
    
    switch (edge) {
      case 0: // Top
        startX = Math.random() * containerRect.width;
        startY = 0;
        break;
      case 1: // Right
        startX = containerRect.width;
        startY = Math.random() * containerRect.height;
        break;
      case 2: // Bottom
        startX = Math.random() * containerRect.width;
        startY = containerRect.height;
        break;
      case 3: // Left
        startX = 0;
        startY = Math.random() * containerRect.height;
        break;
      default:
        startX = 0;
        startY = 0;
    }

    const word = getRandomWord(actualType);
    const newEnemy: Enemy = {
      id: `enemy_${Date.now()}_${Math.random()}`,
      type: actualType,
      word: word,
      obfuscatedSegments: obfuscateWord(word), // Anti-cheat: obfuscated word segments
      x: startX,
      y: startY,
      targetX: centerX,
      targetY: centerY,
      scale: 1,
      isVisible: actualType !== 'rootkit',
      partialText: actualType === 'rootkit' ? createPartialText(word) : undefined,
      spawnTime: Date.now()
    };

    setEnemies(prev => [...prev, newEnemy]);
  }, [gameState.isPlaying, gameState.isPaused, enemyWords, gameState.vlanKeeperActive, gameState.wave]);

  // Move enemies - BALANCED VERSION with FLAT 10 DAMAGE
  const moveEnemies = useCallback(() => {
    if (!gameState.isPlaying || gameState.isPaused) return;

    setEnemies(prev => prev.map(enemy => {
      const dx = enemy.targetX - enemy.x;
      const dy = enemy.targetY - enemy.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < 50) {
        // Enemy reached center - FLAT 10 DAMAGE
        const damage = 10;
        setGameState(prevState => ({
          ...prevState,
          health: Math.max(0, prevState.health - damage)
        }));
        return null; // Mark for removal
      }

      // Handle phishing special movement first
      if (enemy.type === 'phishing') {
        // SLIGHTLY reduced erratic movement for better playability
       const erraticX = Math.sin(Date.now() * 0.008 + enemy.spawnTime * 0.005) * 3.0 + 
                 Math.sin(Date.now() * 0.015) * 1.5; // Layered sine waves
const erraticY = Math.cos(Date.now() * 0.012 + enemy.spawnTime * 0.007) * 2.5 + 
                 Math.cos(Date.now() * 0.020) * 1.0; // Double oscillation
        
        // REDUCED speed progression
        const baseMovement = 1.1; // Slightly slower than before
        const waveSpeedMultiplier = 1 + (gameState.wave - 1) * 0.08; // Reduced from 0.15 to 0.08
        const timeSpeedMultiplier = 1 + Math.min((Date.now() - gameState.sessionStartTime) / 180000, 1) * 0.3; // Reduced from 0.5 to 0.3, longer ramp time
        const speed = baseMovement * waveSpeedMultiplier * timeSpeedMultiplier;
        
        const normalMoveX = (dx / distance) * speed;
        const normalMoveY = (dy / distance) * speed;
        
        const newX = enemy.x + normalMoveX + erraticX;
        const newY = enemy.y + normalMoveY + erraticY;
        const newDistance = Math.sqrt((enemy.targetX - newX) ** 2 + (enemy.targetY - newY) ** 2);
        
        const maxDistance = 600;
        const scale = 1 + (1 - Math.min(newDistance, maxDistance) / maxDistance) * 0.8;
        
        let isVisible = enemy.isVisible;
        let partialText = enemy.partialText;
        
        return {
          ...enemy,
          x: newX,
          y: newY,
          scale,
          isVisible,
          partialText
        };
      }
      
      // Regular enemy movement - BALANCED SPEEDS
      let baseSpeed;
      switch (enemy.type) {
        case 'ddos': baseSpeed = 1.6; break; // Reduced from 2
        case 'mini_bug': baseSpeed = 1.3; break; // Reduced from 1.5
        case 'vlan_keeper': baseSpeed = 0.5; break; // Slightly reduced
        case 'rootkit': baseSpeed = 0.7; break; // Slightly reduced
        case 'ransomware': baseSpeed = 0.9; break; // New, slightly slower
        default: baseSpeed = 0.9; // Reduced from 1
      }
      
      // MUCH more gradual speed increases
      const waveSpeedMultiplier = 1 + (gameState.wave - 1) * 0.08; // Reduced from 0.15 to 0.08
      const timeSpeedMultiplier = 1 + Math.min((Date.now() - gameState.sessionStartTime) / 180000, 1) * 0.3; // Reduced cap and longer ramp
      const speed = baseSpeed * waveSpeedMultiplier * timeSpeedMultiplier;

      const moveX = (dx / distance) * speed;
      const moveY = (dy / distance) * speed;
      
      const newX = enemy.x + moveX;
      const newY = enemy.y + moveY;
      const newDistance = Math.sqrt((enemy.targetX - newX) ** 2 + (enemy.targetY - newY) ** 2);
      
      const maxDistance = 600;
      const scale = 1 + (1 - Math.min(newDistance, maxDistance) / maxDistance) * 0.8;
      
      // Rootkit visibility
      let isVisible = enemy.isVisible;
      let partialText = enemy.partialText;
      if (enemy.type === 'rootkit' && newDistance < 200) {
        isVisible = true;
        partialText = enemy.word;
      }

      return {
        ...enemy,
        x: newX,
        y: newY,
        scale,
        isVisible,
        partialText
      };
    }).filter(Boolean) as Enemy[]);
  }, [gameState.isPlaying, gameState.isPaused, gameState.wave, gameState.sessionStartTime]);

  // Anti-cheat enhanced typing handler
  const handleTyping = (value: string) => {
    const now = Date.now();
    analyzeKeystroke(now);
    
    setCurrentInput(value);
    
    if (value.trim() === '') return;

    // Anti-cheat: Apply penalties for suspicious behavior
    if (isPlayerSuspicious) {
      // Add artificial delay for suspicious players
      setTimeout(() => {
        processTyping(value.toUpperCase().trim(), now);
      }, Math.random() * 500 + 200); // 200-700ms delay
      return;
    }

    processTyping(value.toUpperCase().trim(), now);
  };

  const processTyping = (typedWord: string, timestamp: number) => {
    // Check for special weapon commands first
    if (typedWord === 'NUKE' && gameState.nukesAvailable > 0) {
      // Nuclear option - destroy ALL enemies
      setEnemies([]);
      setGameState(prev => ({
        ...prev,
        nukesAvailable: prev.nukesAvailable - 1,
        score: prev.score + (enemies.length * 50), // Bonus for mass destruction
        enemiesDestroyed: prev.enemiesDestroyed + enemies.length,
        vlanKeeperActive: false, // Disable VLAN if active
        activeQuarter: null
      }));
      setCurrentInput('');
      return;
    }
    
    if (typedWord === 'FIREWALL' && gameState.firewallsAvailable > 0) {
      // Firewall - freeze all enemies for 5 seconds
      setGameState(prev => ({ ...prev, firewallsAvailable: prev.firewallsAvailable - 1 }));
      setCurrentInput('');
      
      // Freeze enemies by setting a temporary flag
      setEnemies(prev => prev.map(enemy => ({ ...enemy, frozen: true } as Enemy & { frozen?: boolean })));
      
      setTimeout(() => {
        setEnemies(prev => prev.map(enemy => {
          const { frozen, ...cleanEnemy } = enemy as Enemy & { frozen?: boolean };
          return cleanEnemy;
        }));
      }, 5000);
      return;
    }
    
    if (typedWord === 'SCAN' && gameState.scanAvailable > 0) {
      // Vulnerability scan - reveal all rootkits and make them visible
      setGameState(prev => ({ ...prev, scanAvailable: prev.scanAvailable - 1 }));
      setEnemies(prev => prev.map(enemy => {
        if (enemy.type === 'rootkit') {
          return { ...enemy, isVisible: true, partialText: enemy.word };
        }
        return enemy;
      }));
      setCurrentInput('');
      return;
    }
    
    // Regular enemy matching logic
    const matchedEnemy = enemies.find(enemy => {
      // First check if the word matches
      if (enemy.word !== typedWord) return false;
      
      // VLAN Keeper logic - only allow typing words in active quarter or VLAN keeper itself
      if (gameState.vlanKeeperActive && gameState.activeQuarter !== null) {
        if (enemy.type === 'vlan_keeper') {
          return true; // Always allow VLAN keeper to be typed
        }
        
        const quarter = getEnemyQuarter(enemy);
        if (quarter !== gameState.activeQuarter) {
          return false;
        }
      }
      
      return true;
    });

    if (matchedEnemy) {
      // Anti-cheat: Track perfect hits
      setAntiCheatMetrics(prev => ({
        ...prev,
        perfectStreak: prev.perfectStreak + 1
      }));

      // Handle special enemy effects
      if (matchedEnemy.type === 'vlan_keeper') {
        // Remove the VLAN Keeper enemy AND disable the effect
        setEnemies(prev => prev.filter(e => e.id !== matchedEnemy.id));
        setGameState(prev => ({
          ...prev,
          vlanKeeperActive: false,
          activeQuarter: null
        }));
      } else if (matchedEnemy.type === 'ransomware') {
        // Explosion effect - remove nearby enemies
        const explosionRadius = 100;
        setEnemies(prev => prev.filter(enemy => {
          if (enemy.id === matchedEnemy.id) return false;
          const distance = Math.sqrt(
            (enemy.x - matchedEnemy.x) ** 2 + (enemy.y - matchedEnemy.y) ** 2
          );
          return distance > explosionRadius;
        }));
      } else if (matchedEnemy.type === 'bug') {
        // Split into mini bugs
        const miniBugs: Enemy[] = [];
        for (let i = 0; i < 2; i++) {
          const word = getRandomWord('mini_bug');
          miniBugs.push({
            id: `minibug_${Date.now()}_${i}`,
            type: 'mini_bug',
            word: word,
            obfuscatedSegments: obfuscateWord(word),
            x: matchedEnemy.x + (Math.random() - 0.5) * 50,
            y: matchedEnemy.y + (Math.random() - 0.5) * 50,
            targetX: matchedEnemy.targetX,
            targetY: matchedEnemy.targetY,
            scale: 0.8,
            isVisible: true,
            spawnTime: Date.now()
          });
        }
        setEnemies(prev => [...prev.filter(e => e.id !== matchedEnemy.id), ...miniBugs]);
      } else {
        // Normal enemy removal
        setEnemies(prev => prev.filter(e => e.id !== matchedEnemy.id));
      }

      // Update stats with anti-cheat considerations
      const now = Date.now();
      setGameState(prev => {
        const newCorrectWords = prev.correctWords + 1;
        const newWordsTyped = prev.wordsTyped + 1;
        
        // Anti-cheat: Reduce score for suspicious players
        const scoreMultiplier = isPlayerSuspicious ? 0.1 : 1;
        const newScore = prev.score + (matchedEnemy.word.length * 10 * prev.wave * scoreMultiplier);
        
        return {
          ...prev,
          score: Math.round(newScore),
          correctWords: newCorrectWords,
          wordsTyped: newWordsTyped,
          enemiesDestroyed: prev.enemiesDestroyed + 1
        };
      });

      setCurrentInput('');
      
      // Update WPM calculation
      if (now - lastWpmUpdate > 5000) {
        const timeMinutes = (now - gameStartTime) / 60000;
        if (timeMinutes > 0) {
          setGameState(prev => ({
            ...prev,
            wpm: Math.round(prev.correctWords / timeMinutes)
          }));
        }
        setLastWpmUpdate(now);
      }
    } else {
      // Anti-cheat: Reset perfect streak on miss
      setAntiCheatMetrics(prev => ({
        ...prev,
        perfectStreak: 0
      }));
    }
  };

  // Get enemy quarter for VLAN logic
  const getEnemyQuarter = (enemy: Enemy): number => {
    const container = gameContainerRef.current;
    if (!container) return 0;
    
    const rect = container.getBoundingClientRect();
    const midX = rect.width / 2;
    const midY = rect.height / 2;
    
    // Use enemy's current position, not container bounds
    if (enemy.x < midX && enemy.y < midY) return 0; // Top-left
    if (enemy.x >= midX && enemy.y < midY) return 1; // Top-right
    if (enemy.x < midX && enemy.y >= midY) return 2; // Bottom-left
    return 3; // Bottom-right
  };

  // Handle VLAN Keeper activation
  useEffect(() => {
    const vlanKeeper = enemies.find(e => e.type === 'vlan_keeper');
    if (vlanKeeper && !gameState.vlanKeeperActive) {
      setGameState(prev => ({
        ...prev,
        vlanKeeperActive: true,
        activeQuarter: Math.floor(Math.random() * 4)
      }));
    }
  }, [enemies, gameState.vlanKeeperActive]);

  // Updated game loop with MUCH RARER power-ups (waves 5, 8, 12)
  useEffect(() => {
    if (gameState.isPlaying && !gameState.isPaused) {
      gameLoopRef.current = window.setInterval(() => {
        moveEnemies();
        
        // BALANCED spawn system - logarithmic scaling instead of linear
        const baseSpawnRate = 0.015; // Slightly reduced base rate
        
        // Logarithmic wave progression instead of linear (much gentler)
        const waveMultiplier = 1 + Math.log(gameState.wave) * 0.4; // Much gentler than direct multiplication
        
        // Slower time-based progression
        const timeMultiplier = 1 + Math.min((Date.now() - gameState.sessionStartTime) / 240000, 1.5) * 0.4; // 4 minutes to cap, lower multiplier
        
        const spawnRate = baseSpawnRate * waveMultiplier * timeMultiplier;
        
        // Cap spawn rate to prevent overwhelming late game
        const cappedSpawnRate = Math.min(spawnRate, 0.08); // Never exceed 8% spawn chance per tick
        
        if (Math.random() < cappedSpawnRate) {
          spawnEnemy();
        }
        
        // UPDATED wave progression - more enemies needed, but with bonuses
        const enemiesNeededForNextWave = Math.ceil(gameState.wave * 6 + Math.log(gameState.wave) * 4); // Scales more gradually
        
        if (gameState.enemiesDestroyed >= enemiesNeededForNextWave) {
          setGameState(prev => { 
            const newWave = prev.wave + 1;
            
            // ENHANCED wave completion bonuses for sustainability
            let healthBonus = 15; // Increased from 10
            let newState = {
              ...prev, 
              wave: newWave,
              health: Math.min(100, prev.health + healthBonus)
            };
            
            // MUCH RARER special weapon rewards - first at waves 5, 8, 12
if (newWave === 5) {
  newState.scanAvailable = prev.scanAvailable + 1; // First scan at layer 5
} else if (newWave === 8) {
  newState.firewallsAvailable = prev.firewallsAvailable + 1; // First firewall at layer 8
} else if (newWave === 6) {
  newState.nukesAvailable = prev.nukesAvailable + 1; // First nuke at layer 6
} else if (newWave === 15) {
  newState.firewallsAvailable = prev.firewallsAvailable + 1;
  newState.scanAvailable = prev.scanAvailable + 1;
} else if (newWave % 6 === 0 && newWave > 6) {
  // Every 6th layer after 6 (12, 18, 24...) - nuke
  newState.nukesAvailable = prev.nukesAvailable + 1;
} else if (newWave % 6 === 0 && newWave > 8) {
  // Every 6th layer after 8 - firewall  
  newState.firewallsAvailable = prev.firewallsAvailable + 1;
} else if (newWave % 4 === 0 && newWave > 5) {
  // Every 4th layer after 5 - scan
  newState.scanAvailable = prev.scanAvailable + 1;
}
            
            // SPECIAL wave milestones with extra bonuses
            if (newWave === 10) {
              newState.health = 100; // Full heal at wave 10
            } else if (newWave === 20) {
              newState.health = 100; // Full heal at wave 20
              newState.nukesAvailable = prev.nukesAvailable + 1; // Bonus nuke
            } else if (newWave % 15 === 0) {
              // Every 15th wave - major bonus
              newState.health = 100;
              newState.nukesAvailable = prev.nukesAvailable + 1;
            }
            
            return newState;
          });
        }
        
        // Check game over
        if (gameState.health <= 0) {
          endGame();
        }
      }, 50);
    }

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gameState.isPlaying, gameState.isPaused, moveEnemies, spawnEnemy, gameState.enemiesDestroyed, gameState.wave, gameState.health, gameState.sessionStartTime]);

  // Start game
  const startGame = () => {
    const now = Date.now();
    setGameStartTime(now);
    setLastWpmUpdate(now);
    
    // Anti-cheat: Reset metrics for new game
    setAntiCheatMetrics({
      keystrokes: [],
      lastKeystroke: 0,
      perfectStreak: 0,
      averageInterval: 0,
      suspiciousPatterns: [],
      rapidFires: 0
    });
    setIsPlayerSuspicious(false);
    
    setGameState(prev => ({
      ...prev,
      isPlaying: true,
      isPaused: false,
      sessionStartTime: now,
      score: 0,
      wave: 1,
      health: 100,
      wpm: 0,
      wordsTyped: 0,
      correctWords: 0,
      enemiesDestroyed: 0,
      vlanKeeperActive: false,
      activeQuarter: null,
      nukesAvailable: 0,
      firewallsAvailable: 0,
      scanAvailable: 0
    }));
    setEnemies([]);
    setCurrentInput('');
    setShowLeaderboard(false);
    setShowHelp(false);
    
    // Focus input
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Anti-cheat enhanced endGame with server validation
  const endGame = async () => {
    setGameState(prev => ({ ...prev, isPlaying: false, isPaused: false }));
    
    if (!profile?.user_id) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const sessionDuration = (Date.now() - gameState.sessionStartTime) / 1000;
      const startedAt = new Date(gameState.sessionStartTime);
      const endedAt = new Date();
      
      // Anti-cheat: Server-side validation data
      const suspiciousFlags = [];
      if (gameState.wpm > 200) suspiciousFlags.push('excessive_wpm');
      if (antiCheatMetrics.perfectStreak > 20) suspiciousFlags.push('perfect_streak');
      if (antiCheatMetrics.suspiciousPatterns.length > 0) suspiciousFlags.push('robotic_patterns');
      if (gameState.wpm > 150 && sessionDuration < 60) suspiciousFlags.push('impossible_speed');
      
      const sessionData = {
        user_id: profile.user_id,
        score: isPlayerSuspicious ? Math.round(gameState.score * 0.1) : gameState.score, // Penalize suspicious scores
        wpm: Math.min(gameState.wpm, 200), // Cap WPM at reasonable limit
        words_typed: gameState.wordsTyped,
        waves_survived: gameState.wave,
        duration_seconds: Math.round(sessionDuration),
        started_at: startedAt.toISOString().replace('T', ' ').replace('Z', ''),
        ended_at: endedAt.toISOString().replace('T', ' ').replace('Z', ''),
        // Store anti-cheat metadata (optional - add to schema if needed)
        // suspicious_flags: suspiciousFlags.join(','),
        // avg_keystroke_interval: antiCheatMetrics.averageInterval
      };
      
      await supabase.from('rapid_response_sessions').insert(sessionData);
    } catch (error) {
      // Silent error handling
    }
  };

  // Pause/resume
  const togglePause = () => {
    setGameState(prev => ({ ...prev, isPaused: !prev.isPaused }));
  };

  // Auth loading screen
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading Rapid Response...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <TSOAuthDetector />
        <div className="text-center max-w-md mx-auto p-8">
          <h1 className="text-4xl font-bold text-white mb-4 font-['Orbitron',_sans-serif]">
            Rapid Response
          </h1>
          <p className="text-gray-400 mb-8">
            SO Cybersecurity Typing Defense Game
          </p>
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p className="text-gray-300 mb-4">
              Please log in through Shielded Onion platform to access Rapid Response.
            </p>
            <a
              href="https://accounts.shieldedonion.co.uk"
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Login to Shielded Onion
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Main game interface
  return (
    <div className="min-h-screen bg-gray-900 text-gray-200">
      <TSOAuthDetector />
      
      {/* Anti-cheat warning */}
      {isPlayerSuspicious && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-red-900/90 border border-red-500 text-red-200 px-4 py-2 rounded-lg z-50">
          ⚠️ Unusual typing patterns detected - Score penalties applied
        </div>
      )}
      
      {/* Header */}
      <header className="bg-gray-800 p-4 shadow-md flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-bold text-white font-['Orbitron',_sans-serif]">
            Rapid Response
          </h1>
          <div className="text-sm">
            Player: <span className="text-blue-400 font-semibold">{profile?.username}</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="text-gray-300 hover:text-white flex items-center"
          >
            <HelpCircle className="w-4 h-4 mr-1" />
            Help
          </button>
          
          <button
            onClick={() => setShowLeaderboard(!showLeaderboard)}
            className="text-gray-300 hover:text-white flex items-center"
          >
            <Trophy className="w-4 h-4 mr-1" />
            Leaderboard
          </button>
          
          <a
            href="https://shieldedonion.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-300 hover:text-white flex items-center"
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            Shielded Onion
          </a>
          
          <a
            href="https://threat-command.shieldedonion.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-300 hover:text-white flex items-center"
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            Threat Command
          </a>
          
          <button
            onClick={logout}
            className="text-gray-300 hover:text-white flex items-center"
          >
            <LogOut className="w-4 h-4 mr-1" />
            Logout
          </button>
        </div>
      </header>

      {/* Game Container */}
      <div 
        ref={gameContainerRef}
        className="relative w-full h-[calc(100vh-200px)] bg-gradient-radial from-gray-800 to-gray-900 overflow-hidden"
      >
        {/* Player in center */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg flex items-center justify-center animate-pulse z-10">
          <span className="text-2xl">🛡️</span>
        </div>

        {/* VLAN Keeper sections */}
        {gameState.vlanKeeperActive && (
          <div className="absolute inset-0 pointer-events-none z-5">
            {[0, 1, 2, 3].map(quarter => (
              <div
                key={quarter}
                className={`absolute border-4 rounded-lg ${quarter === gameState.activeQuarter 
                  ? 'border-orange-400 bg-orange-900/30 animate-pulse' 
                  : 'border-red-600 bg-red-900/50'
                }`}
                style={{
                  top: quarter < 2 ? '5%' : '52%',
                  left: quarter % 2 === 0 ? '5%' : '52%',
                  width: '43%',
                  height: '43%'
                }}
              >
                {/* Quarter label */}
                <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold ${
                  quarter === gameState.activeQuarter 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-red-600 text-white'
                }`}>
                  {quarter === gameState.activeQuarter ? 'ACTIVE' : 'BLOCKED'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Anti-cheat enhanced enemies */}
        {enemies.map(enemy => {
          const enemyStyle = ENEMY_TYPES[enemy.type as keyof typeof ENEMY_TYPES];
          const isInActiveQuarter = !gameState.vlanKeeperActive || 
            enemy.type === 'vlan_keeper' || 
            getEnemyQuarter(enemy) === gameState.activeQuarter;
          
          return (
            <div
              key={enemy.id}
              className={`absolute flex flex-col items-center transition-all duration-100 ${randomClasses.enemyContainer} ${
                isInActiveQuarter ? 'opacity-100' : 'opacity-20'
              }`}
              style={{
                left: enemy.x - 30,
                top: enemy.y - 40,
                transform: `scale(${enemy.scale})`,
                zIndex: Math.round(enemy.scale * 10)
              }}
            >
              <div className={`w-8 h-8 rounded-full ${enemyStyle.bgColor} ${enemyStyle.borderColor} border-2 flex items-center justify-center mb-1 ${randomClasses.enemyIcon} ${
                !isInActiveQuarter ? 'grayscale' : ''
              }`}>
                <span className="text-lg">{enemyStyle.icon}</span>
              </div>
              
              {/* Anti-cheat: Obfuscated word display */}
              <div className={`px-2 py-1 rounded text-xs font-mono font-bold whitespace-nowrap ${enemyStyle.bgColor} ${enemyStyle.borderColor} border ${enemyStyle.color} ${randomClasses.enemyText} ${
                !isInActiveQuarter ? 'line-through grayscale' : ''
              }`}>
                {enemy.isVisible ? (
                  // Render obfuscated segments with random delays and hidden elements
                  <span style={{position: 'relative'}}>
                    {enemy.obfuscatedSegments.map((segment, index) => (
                      <React.Fragment key={segment.id}>
                        <span 
                          style={{
                            animationDelay: `${segment.delay}ms`,
                            position: 'relative'
                          }}
                          data-decoy={Math.random().toString(36)}
                        >
                          {segment.char}
                        </span>
                        {index === Math.floor(enemy.obfuscatedSegments.length / 2) && (
                          <span style={{position: 'absolute', left: '-9999px', opacity: 0}}>
                            DECOY{Math.random().toString(36)}
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                    {/* Hidden decoy text */}
                    <div style={{display: 'none'}} data-fake-word={`FAKE${Math.random().toString(36)}`}>
                      {enemy.word.split('').reverse().join('')}
                    </div>
                  </span>
                ) : (
                  enemy.partialText || '???'
                )}
              </div>
            </div>
          );
        })}

        {/* HUD Elements */}
        <div className="absolute top-4 left-4 space-y-2 z-20">
          <div className="bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 border border-gray-700">
            <div className="text-sm">Layer: <span className="text-blue-400 font-bold">{gameState.wave}</span></div>
            <div className="text-sm">Score: <span className="text-yellow-400 font-bold">{gameState.score.toLocaleString()}</span></div>
            <div className="text-sm">WPM: <span className="text-green-400 font-bold">{gameState.wpm}</span></div>
            {/* Anti-cheat: Show debug info in development */}
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-red-400 mt-1">
                Suspicious: {isPlayerSuspicious ? 'YES' : 'NO'}
              </div>
            )}
            <div className="text-xs text-gray-400 mt-1">
              Speed: {((1 + (gameState.wave - 1) * 0.08) * (1 + Math.min((Date.now() - gameState.sessionStartTime) / 180000, 1) * 0.3)).toFixed(1)}x
            </div>
            {gameState.isPlaying && (
              <div className="text-xs text-gray-400">
                Next layer: {Math.ceil(gameState.wave * 6 + Math.log(gameState.wave) * 4) - gameState.enemiesDestroyed} enemies
              </div>
            )}
          </div>
        </div>

        <div className="absolute top-4 right-4 space-y-2 z-20">
          <div className="bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 border border-gray-700 text-right">
            <div className="text-sm">Words: <span className="text-purple-400 font-bold">{gameState.correctWords}</span></div>
          </div>
        </div>

        <div className="absolute bottom-20 left-4 z-20">
          <div className="bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 border border-gray-700">
            <div className="text-sm mb-2 text-green-400">Special Weapons</div>
            <div className="space-y-1 text-xs">
              <div className={`flex justify-between ${gameState.nukesAvailable > 0 ? 'text-red-400' : 'text-gray-500'}`}>
                <span>💣 NUKE:</span>
                <span>{gameState.nukesAvailable}</span>
              </div>
              <div className={`flex justify-between ${gameState.firewallsAvailable > 0 ? 'text-blue-400' : 'text-gray-500'}`}>
                <span>🛡️ FIREWALL:</span>
                <span>{gameState.firewallsAvailable}</span>
              </div>
              <div className={`flex justify-between ${gameState.scanAvailable > 0 ? 'text-purple-400' : 'text-gray-500'}`}>
                <span>🔍 SCAN:</span>
                <span>{gameState.scanAvailable}</span>
              </div>
            </div>
            {(gameState.nukesAvailable > 0 || gameState.firewallsAvailable > 0 || gameState.scanAvailable > 0) && (
              <div className="text-xs text-gray-400 mt-2 border-t border-gray-600 pt-2">
                Type command name to use
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-20 right-4 z-20">
          <div className="bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 border border-gray-700">
            <div className="text-sm mb-2">Health</div>
            <div className="w-32 h-3 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 transition-all duration-300"
                style={{width: `${Math.round(gameState.health)}%`}}
              />
            </div>
            <div className="text-xs text-center mt-1">{Math.round(gameState.health)}/100</div>
          </div>
        </div>

        {/* Start Game Overlay */}
        {!gameState.isPlaying && gameState.score === 0 && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 text-center max-w-md">
              <h2 className="text-3xl font-bold mb-4 font-['Orbitron',_sans-serif]">Ready to Defend?</h2>
              <p className="text-gray-300 mb-6">
                Type the words attached to enemies to eliminate cyber threats. 
                Survive waves of attacks and climb the leaderboard!
              </p>
              <div className="mb-6 text-sm text-gray-400">
                <p>🦠 Malware • ⚡ DDoS • 🎣 Phishing • 🔑 Rootkit</p>
                <p>🔒 Ransomware • 🐛 Bugs • 🔀 VLAN Keeper</p>
              </div>
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={startGame}
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold text-lg"
                >
                  Start Mission
                </button>
                <button
                  onClick={() => setShowHelp(true)}
                  className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-semibold text-lg"
                >
                  How to Play
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Game Over Overlay */}
        {!gameState.isPlaying && gameState.score > 0 && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 text-center">
              <h2 className="text-2xl font-bold mb-4">Mission Complete!</h2>
              <div className="space-y-2 mb-6">
                <p>Final Score: <span className="text-yellow-400 font-bold">{gameState.score.toLocaleString()}</span></p>
                <p>Layers Survived: <span className="text-blue-400 font-bold">{gameState.wave}</span></p>
                <p>Words Per Minute: <span className="text-green-400 font-bold">{gameState.wpm}</span></p>
                {isPlayerSuspicious && (
                  <p className="text-red-400 text-sm">⚠️ Score adjusted for suspicious activity</p>
                )}
              </div>
              <div className="flex space-x-4 justify-center">
                <button
                  onClick={startGame}
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded font-semibold"
                >
                  Play Again
                </button>
                <button
                  onClick={() => setShowLeaderboard(true)}
                  className="bg-gray-600 hover:bg-gray-700 px-6 py-2 rounded font-semibold"
                >
                  View Leaderboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pause Overlay */}
        {gameState.isPaused && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-40">
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 text-center">
              <h2 className="text-xl font-bold mb-4">Mission Paused</h2>
              <button
                onClick={togglePause}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
              >
                Resume
              </button>
            </div>
          </div>
        )}

        {/* Help/Tutorial Overlay */}
        {showHelp && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white font-['Orbitron',_sans-serif]">
                  🛡️ Rapid Response Guide
                </h2>
                <button
                  onClick={() => setShowHelp(false)}
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ✕
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Basic Gameplay */}
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-lg font-bold text-blue-400 mb-3">🎯 Basic Gameplay</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong className="text-green-400">Objective:</strong> Defend your system by typing enemy words</p>
                    <p><strong className="text-green-400">Controls:</strong> Type any word on screen to eliminate threats</p>
                    <p><strong className="text-green-400">Health:</strong> You lose 10 health when enemies reach the center</p>
                    <p><strong className="text-green-400">Waves:</strong> Survive increasingly difficult layers of cyber attacks</p>
                    <p><strong className="text-green-400">Scoring:</strong> Points = Word Length × 10 × layer Number</p>
                  </div>
                </div>

                {/* Special Weapons */}
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-lg font-bold text-red-400 mb-3">⚔️ Special Weapons</h3>
                  <div className="space-y-3 text-sm">
                    <div className="border-l-4 border-red-400 pl-3">
                      <p><strong className="text-red-400">💣 NUKE</strong></p>
                      <p>Type "NUKE" to destroy ALL enemies instantly</p>
                      <p className="text-gray-400">• Earned at layer 6, then every 6th layer</p>
                      <p className="text-gray-400">• Bonus points for mass destruction</p>
                    </div>
                    <div className="border-l-4 border-blue-400 pl-3">
                      <p><strong className="text-blue-400">🛡️ FIREWALL</strong></p>
                      <p>Type "FIREWALL" to freeze all enemies for 5 seconds</p>
                      <p className="text-gray-400">• Earned at layer 8, then every 6th layer</p>
                      <p className="text-gray-400">• Perfect for overwhelming situations</p>
                    </div>
                    <div className="border-l-4 border-purple-400 pl-3">
                      <p><strong className="text-purple-400">🔍 SCAN</strong></p>
                      <p>Type "SCAN" to reveal all hidden rootkits</p>
                      <p className="text-gray-400">• Earned at layer 5, then every 4th layer</p>
                      <p className="text-gray-400">• Makes rootkits visible and targetable</p>
                    </div>
                  </div>
                </div>

                {/* Enemy Types */}
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-lg font-bold text-yellow-400 mb-3">🦠 Enemy Types</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">🎣</span>
                      <div>
                        <strong className="text-purple-400">Phishing</strong>
                        <p className="text-gray-400">Erratic movement, most common (35%)</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">🦠</span>
                      <div>
                        <strong className="text-red-400">Malware</strong>
                        <p className="text-gray-400">Standard speed, common (20%)</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">⚡</span>
                      <div>
                        <strong className="text-yellow-400">DDoS</strong>
                        <p className="text-gray-400">Fast moving attacks (20%)</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">🔒</span>
                      <div>
                        <strong className="text-red-500">Ransomware</strong>
                        <p className="text-gray-400">Explodes, destroying nearby enemies (10%)</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">🐛</span>
                      <div>
                        <strong className="text-green-400">Bug</strong>
                        <p className="text-gray-400">Splits into 2 mini-bugs when destroyed (7%)</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">🔑</span>
                      <div>
                        <strong className="text-gray-400">Rootkit</strong>
                        <p className="text-gray-400">Hidden until close, slow moving (5%)</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">🔀</span>
                      <div>
                        <strong className="text-orange-400">VLAN Keeper</strong>
                        <p className="text-gray-400">Blocks network segments, rare (3%)</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Advanced Mechanics */}
                <div className="bg-gray-900 p-4 rounded-lg">
                  <h3 className="text-lg font-bold text-cyan-400 mb-3">🎮 Advanced Mechanics</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <strong className="text-orange-400">VLAN Keeper Effect:</strong>
                      <p className="text-gray-400">When active, divides screen into 4 quarters. Only enemies in the highlighted quarter can be targeted. Defeat the VLAN Keeper to restore normal targeting.</p>
                    </div>
                    <div>
                      <strong className="text-green-400">Wave Bonuses:</strong>
                      <p className="text-gray-400">Complete layers to gain +15 health. Full heal at waves 10, 20, and every 15th wave. Special weapons are rare rewards!</p>
                    </div>
                    <div>
                      <strong className="text-blue-400">Progression:</strong>
                      <p className="text-gray-400">Enemy speed and spawn rate increase gradually. Use special weapons strategically to survive longer waves.</p>
                    </div>
                    <div>
                      <strong className="text-purple-400">Scoring Tips:</strong>
                      <p className="text-gray-400">Longer words = more points. Higher waves = point multipliers. Type quickly for better WPM and leaderboard ranking.</p>
                    </div>
                    <div>
                      <strong className="text-red-400">Anti-Cheat:</strong>
                      <p className="text-gray-400">The system monitors for unusual typing patterns. Automated scripts will be detected and penalized.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Strategy Tips */}
              <div className="bg-gray-900 p-4 rounded-lg mt-6">
                <h3 className="text-lg font-bold text-green-400 mb-3">💡 Pro Tips</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p><strong>• Priority Targeting:</strong> Focus on fast enemies (DDoS) and close threats first</p>
                    <p><strong>• Save Weapons:</strong> Special weapons are rare - use them wisely!</p>
                    <p><strong>• VLAN Strategy:</strong> When VLAN Keeper appears, clear the active quarter first</p>
                  </div>
                  <div>
                    <p><strong>• Rootkit Detection:</strong> Use SCAN when you suspect hidden enemies</p>
                    <p><strong>• Emergency Protocol:</strong> Use FIREWALL when overwhelmed, then NUKE</p>
                    <p><strong>• Wave Planning:</strong> Special weapons come at waves 5, 8, and 12 first</p>
                  </div>
                </div>
              </div>

              {/* Controls Reference */}
              <div className="bg-blue-900/20 p-4 rounded-lg mt-6 border border-blue-500">
                <h3 className="text-lg font-bold text-blue-400 mb-2">⌨️ Quick Reference</h3>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p><strong>ESC/PAUSE:</strong> Pause game</p>
                    <p><strong>Type words:</strong> Eliminate enemies</p>
                  </div>
                  <div>
                    <p><strong>NUKE:</strong> Destroy all enemies</p>
                    <p><strong>FIREWALL:</strong> Freeze all enemies</p>
                  </div>
                  <div>
                    <p><strong>SCAN:</strong> Reveal rootkits</p>
                    <p><strong>Tab:</strong> View leaderboards</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowHelp(false)}
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded font-semibold"
                >
                  Ready to Defend! 🛡️
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Overlay */}
        {showLeaderboard && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Rapid Response Leaderboards</h2>
                <button
                  onClick={() => setShowLeaderboard(false)}
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ✕
                </button>
              </div>

              {leaderboardLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-400">Loading leaderboards...</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Daily Leaderboard */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-blue-400">🏆 Daily Leaderboard</h3>
                    <div className="bg-gray-900 rounded-lg p-4">
                      {leaderboardData.userRank.daily && (
                        <div className="mb-3 p-2 bg-blue-900/30 rounded border border-blue-500">
                          <p className="text-sm text-blue-300">Your Rank: #{leaderboardData.userRank.daily}</p>
                        </div>
                      )}
                      <div className="space-y-2">
                        {leaderboardData.daily.length > 0 ? (
                          leaderboardData.daily.map((entry, index) => (
                            <div key={entry.user_id} className="flex justify-between items-center p-2 bg-gray-800 rounded">
                              <div className="flex items-center space-x-3">
                                <span className={`font-bold text-lg ${
                                  index === 0 ? 'text-yellow-400' : 
                                  index === 1 ? 'text-gray-300' : 
                                  index === 2 ? 'text-amber-600' : 'text-gray-400'
                                }`}>
                                  #{entry.rank}
                                </span>
                                <div>
                                  <p className="font-semibold">{entry.profiles?.username || 'Unknown'}</p>
                                  <p className="text-xs text-gray-400">
                                    {entry.profiles?.faction_id === 1 ? '🛡️ Defender' : '⚔️ Attacker'}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-yellow-400">{entry.best_score?.toLocaleString()}</p>
                                <p className="text-xs text-gray-400">
                                  {entry.best_wpm} WPM
                                </p>
                                {entry.cc_awarded > 0 && (
                                  <p className="text-xs text-green-400">+{entry.cc_awarded} CC</p>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-gray-400 py-4">No daily scores yet</p>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Top 3 daily players earn CC rewards!</p>
                  </div>

                  {/* Weekly Leaderboard */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-purple-400">📅 Weekly Leaderboard</h3>
                    <div className="bg-gray-900 rounded-lg p-4">
                      {leaderboardData.userRank.weekly && (
                        <div className="mb-3 p-2 bg-purple-900/30 rounded border border-purple-500">
                          <p className="text-sm text-purple-300">Your Rank: #{leaderboardData.userRank.weekly}</p>
                        </div>
                      )}
                      <div className="space-y-2">
                        {leaderboardData.weekly.length > 0 ? (
                          leaderboardData.weekly.map((entry, index) => (
                            <div key={entry.user_id} className="flex justify-between items-center p-2 bg-gray-800 rounded">
                              <div className="flex items-center space-x-3">
                                <span className={`font-bold text-lg ${
                                  index === 0 ? 'text-yellow-400' : 
                                  index === 1 ? 'text-gray-300' : 
                                  index === 2 ? 'text-amber-600' : 'text-gray-400'
                                }`}>
                                  #{entry.rank}
                                </span>
                                <div>
                                  <p className="font-semibold">{entry.profiles?.username || 'Unknown'}</p>
                                  <p className="text-xs text-gray-400">
                                    {entry.profiles?.faction_id === 1 ? '🛡️ Defender' : '⚔️ Attacker'} • {entry.total_sessions} games
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-yellow-400">{entry.best_score?.toLocaleString()}</p>
                                <p className="text-xs text-gray-400">
                                  {entry.best_wpm} WPM
                                </p>
                                {entry.cc_awarded > 0 && (
                                  <p className="text-xs text-green-400">+{entry.cc_awarded} CC</p>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-gray-400 py-4">No weekly scores yet</p>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Weekly rewards coming soon!</p>
                  </div>
                </div>
              )}

              <div className="mt-6 text-center">
                <button
                  onClick={fetchLeaderboardData}
                  disabled={leaderboardLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-4 py-2 rounded font-semibold mr-4"
                >
                  {leaderboardLoading ? 'Refreshing...' : 'Refresh'}
                </button>
                <p className="text-xs text-gray-400 mt-2">
                  Leaderboards update automatically. Top daily players earn CC for Threat Command!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Game Controls */}
      {gameState.isPlaying && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-30">
          <div className="flex space-x-2">
            <button
              onClick={togglePause}
              className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded text-sm font-semibold"
            >
              {gameState.isPaused ? 'Resume' : 'Pause'}
            </button>
            <button
              onClick={endGame}
              className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded text-sm font-semibold"
            >
              End Mission
            </button>
          </div>
        </div>
      )}

      {/* Typing Interface */}
      {!showHelp && !showLeaderboard && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800/95 backdrop-blur-sm border-t-2 border-blue-500 p-4 z-30">
          <div className="max-w-2xl mx-auto">
            <div className="text-center text-sm text-gray-400 mb-2">
              {isPlayerSuspicious && (
                <div className="text-red-400 mb-1">⚠️ Anti-cheat active - Penalties applied</div>
              )}
              {gameState.vlanKeeperActive 
                ? `🔀 VLAN KEEPER ACTIVE - Only section ${gameState.activeQuarter! + 1} targets available!`
                : gameState.nukesAvailable > 0 || gameState.firewallsAvailable > 0 || gameState.scanAvailable > 0
                ? 'Type enemy words OR special commands (NUKE, FIREWALL, SCAN)'
                : 'Type any word on screen to eliminate the threat'
              }
            </div>
            <input
              ref={inputRef}
              type="text"
              value={currentInput}
              onChange={(e) => handleTyping(e.target.value)}
              className="w-full px-4 py-3 text-lg font-mono bg-gray-900 border-2 border-gray-600 rounded-lg text-gray-200 focus:border-blue-500 focus:outline-none"
              placeholder="Enter security command..."
              disabled={!gameState.isPlaying || gameState.isPaused}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default App;